import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";
import { useSelector } from "react-redux";
import { WebSocketContext } from "../../../App";
import wsSend_request from "../../../Api/ws/ws_request";
import "../warranty.css";

// How long to wait for the product list before saying it cannot be reached.
const WAIT_MS = 10000;

export const NO_PRODUCT_LIST_TEXT =
  "This registration needs a product. Approve it from the OMS once a product is linked.";

// No portal here: style.css pins .godown-select__menu-portal under the modal (z-index 99), so a
// portalled menu would open behind this dialog. A fixed-position menu inside the modal is not clipped
// by the scrolling body and keeps the menu's own z-index.
const selectStyles = {
  option: (provided) => ({ ...provided, fontSize: "13px" }),
  control: (provided) => ({ ...provided, fontSize: "13px" }),
};

// The OMS product list that the other admin screens use: WebSocket dispatch `product_list`
// (VAR_URLS['product_list'] in app/globals.py), limit 5000, answered into `productlistall`.
// The store is persisted, so an old list may already be there; only an answer that arrives after
// this request counts. `state` is "loading", "ready" or "unavailable" (no fresh answer, or an
// empty one, within WAIT_MS).
export const useProductOptions = (enabled) => {
  const { websocket } = useContext(WebSocketContext) || {};
  const { device_id: deviceId, productlistall } = useSelector((state) => state.adminReducers);
  const [timedOut, setTimedOut] = useState(false);
  const [baseline, setBaseline] = useState(null);
  const latest = useRef(productlistall);
  latest.current = productlistall;

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    setTimedOut(false);
    const timer = setTimeout(() => setTimedOut(true), WAIT_MS);
    return () => clearTimeout(timer);
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !websocket) {
      return;
    }
    setBaseline({ list: latest.current });
    wsSend_request(websocket, {
      transmit: "broadcast",
      url: "product_list",
      request: { status: true, limit: 5000, page_no: 1, search: "" },
      DeviceId: deviceId,
    });
  }, [enabled, websocket, deviceId]);

  const fresh = baseline !== null && productlistall !== baseline.list;
  const options = useMemo(
    () =>
      (Array.isArray(productlistall) ? productlistall : [])
        .filter(
          (product) =>
            product?.is_bike === true &&
            typeof product?.id === "string" &&
            typeof product?.product_name === "string" &&
            product.product_name !== ""
        )
        .map((product) => ({
          value: product.id,
          label: product.sku ? `${product.product_name} (${product.sku})` : product.product_name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [productlistall]
  );

  let state = "loading";
  if (fresh && options.length > 0) {
    state = "ready";
  } else if (timedOut || (fresh && options.length === 0)) {
    state = "unavailable";
  }
  return { options, state };
};

const ProductPicker = ({ options, state, value, onChange, disabled, required }) => {
  if (state === "unavailable") {
    // When the product is optional the person can still approve, so there is nothing to warn about.
    return required ? (
      <div className="warranty-banner is-warning" role="alert">
        <span>{NO_PRODUCT_LIST_TEXT}</span>
      </div>
    ) : null;
  }
  const selected = options.find((option) => option.value === value) || null;
  return (
    <Form.Group className="mt-2" controlId="warrantyReviewProduct">
      <Form.Label>{required ? "Product (needed to approve)" : "Product (only if the OMS has no sale for this frame)"}</Form.Label>
      <div className="selectMultiselect">
        <Select
          inputId="warrantyReviewProduct"
          value={selected}
          onChange={(option) => onChange(option ? option.value : "")}
          options={options}
          isClearable
          isLoading={state === "loading"}
          isDisabled={disabled || state === "loading"}
          className="godown-select-container"
          classNamePrefix="godown-select"
          styles={selectStyles}
          placeholder={state === "loading" ? "Loading products" : "Choose the product"}
          menuPlacement="auto"
          menuPosition="fixed"
        />
      </div>
    </Form.Group>
  );
};

export default ProductPicker;
